import { useCallback, useEffect, useRef } from "react";
import {
  ACTOR_TIMER_STATUS_UI_ENUM,
  CreateActorTimerRequest,
} from "../api/Reports";
import { useCreateReportTimer } from "../api/ReportsApi";
import { formattedISOUTC } from "../utils";
import { WORKFLOW_STEP_STATUS_ENUM } from "../api/Workflows";

type WorkflowStepTimerSession = {
  phase: "idle" | "open" | "ended";
  pendingAbandon?: number;
  openedAtMs?: number;
};

const workflowStepTimerSessions = new Map<string, WorkflowStepTimerSession>();

function compositeSessionKey(
  workflowRunUuid: string,
  stepName: WORKFLOW_STEP_STATUS_ENUM
): string {
  return `${workflowRunUuid}::${stepName}`;
}

function getSession(sessionKey: string): WorkflowStepTimerSession {
  const existing = workflowStepTimerSessions.get(sessionKey);
  if (existing) return existing;

  const session: WorkflowStepTimerSession = { phase: "idle" };
  workflowStepTimerSessions.set(sessionKey, session);
  return session;
}

function clearPendingAbandon(session: WorkflowStepTimerSession) {
  if (session.pendingAbandon !== undefined) {
    clearTimeout(session.pendingAbandon);
    session.pendingAbandon = undefined;
  }
}

function postTimerOnce(
  workflowRunUuid: string,
  stepName: WORKFLOW_STEP_STATUS_ENUM,
  status: ACTOR_TIMER_STATUS_UI_ENUM,
  sendTimer: (input: CreateActorTimerRequest) => void
) {
  const sessionKey = compositeSessionKey(workflowRunUuid, stepName);
  const session = getSession(sessionKey);

  if (status === ACTOR_TIMER_STATUS_UI_ENUM.OPEN) {
    clearPendingAbandon(session);
    if (session.phase === "open") return;
    session.phase = "open";
    session.openedAtMs = Date.now();
  } else {
    if (session.phase !== "open") return;
    clearPendingAbandon(session);
    session.phase = "ended";
  }

  const finishedAt = formattedISOUTC(new Date().toISOString());
  let value: number;
  if (status === ACTOR_TIMER_STATUS_UI_ENUM.OPEN) {
    value = 0;
  } else {
    const openedAt = session.openedAtMs;
    value = openedAt !== undefined ? Math.max(0, Date.now() - openedAt) : 0;
    session.openedAtMs = undefined;
  }
  sendTimer({
    name: stepName,
    value,
    workflow_run_uuid: workflowRunUuid,
    finished_at: finishedAt,
    status,
  });
}

function scheduleAbandon(
  workflowRunUuid: string,
  stepName: WORKFLOW_STEP_STATUS_ENUM,
  sendTimer: (input: CreateActorTimerRequest) => void
) {
  const sessionKey = compositeSessionKey(workflowRunUuid, stepName);
  const session = getSession(sessionKey);
  if (session.phase !== "open") return;

  clearPendingAbandon(session);
  session.pendingAbandon = window.setTimeout(() => {
    session.pendingAbandon = undefined;
    postTimerOnce(
      workflowRunUuid,
      stepName,
      ACTOR_TIMER_STATUS_UI_ENUM.ABANDONED,
      sendTimer
    );
  }, 0);
}

export function resetWorkflowStepActorTimerSessionsForTests(): void {
  if (process.env.NODE_ENV !== "test") {
    throw new Error(
      "resetWorkflowStepActorTimerSessionsForTests is only available in tests"
    );
  }
  workflowStepTimerSessions.clear();
}

export function useWorkflowStepActorTimer(
  workflowRunUuid: string | undefined,
  stepName: WORKFLOW_STEP_STATUS_ENUM
) {
  const { mutate: createReportTimer } = useCreateReportTimer();
  const createReportTimerRef = useRef(createReportTimer);
  createReportTimerRef.current = createReportTimer;

  const postTimer = useCallback(
    (status: ACTOR_TIMER_STATUS_UI_ENUM) => {
      if (!workflowRunUuid) return;
      postTimerOnce(workflowRunUuid, stepName, status, (input) =>
        createReportTimerRef.current(input)
      );
    },
    [workflowRunUuid, stepName]
  );

  useEffect(() => {
    postTimer(ACTOR_TIMER_STATUS_UI_ENUM.OPEN);
  }, [postTimer]);

  useEffect(() => {
    if (!workflowRunUuid) return;

    const sessionKey = compositeSessionKey(workflowRunUuid, stepName);
    clearPendingAbandon(getSession(sessionKey));
    return () => {
      scheduleAbandon(workflowRunUuid, stepName, (input) =>
        createReportTimerRef.current(input)
      );
    };
  }, [workflowRunUuid, stepName]);

  return postTimer;
}
